import {
    type Message,
    type AgentMessageFragment,
    type UserMessage,
    type AgentMessage,
    isUserMessage,
    isAgentResponse,
    type AgentMessageFragmentTodoItem,
} from "@/hooks/use-stream-conversation";
import { AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";

export type GroupedMessage = UserMessage | AgentMessage | AgentTodoGroup;

export type AgentTodoGroup = {
    type: "todo-group";
    groupId: string;
    todos: AgentTodoGroupItem[];
    isContinuation: boolean;
};

export type AgentTodoGroupItem = {
    todoId: string;
    todoTitle: string;
    todoStatus: AgentResponseBlock_TodoItem_Phase;
    fragments: AgentMessageFragment[];
};

/**
 * Groups messages by todo items
 *
 * Todos are grouped based on when they were created together.
 * The agent typically creates multiple todos at once (via todo_reset or todo_write),
 * and clears them together (via todo_clear or todo_reset).
 *
 * A TODO group never contains user messages. Agent messages that aren't part of a
 * group are returned as a stand-alone agent message with an updated ID.
 *
 * If the TODOs in a group were created in a previous group then we're marking the group as a
 * continuation of a previous group; this can happen if e.g. the agent asks for clarification on
 * how to solve a specific todo item and the user responds. The user message will split the group
 * and the 2nd todo group will be marked as a continuation of the first.
 *
 * @param messages - The messages to group
 */
export function groupMessagesByTodo(messages: Message[]): GroupedMessage[] {
    const results: GroupedMessage[] = [];
    let groupIndex = 0;

    // Tracks TODO IDs that have been assigned to a group. Used to detect
    // continuation groups - if a TODO ID is already in this set when we
    // create a new group, that group is continuing work from a previous group.
    const seenTodoIds = new Set<string>();

    for (const message of messages) {
        if (isUserMessage(message)) {
            results.push(message);
            continue;
        }

        if (isAgentResponse(message)) {
            const grouped = groupAgentMessagesByTodo(message, groupIndex, seenTodoIds);
            results.push(...grouped.results);
            groupIndex = grouped.nextGroupIndex;
        }
    }

    return applyFinalTodoStates(results, messages);
}

function groupAgentMessagesByTodo(
    message: AgentMessage,
    groupIndex: number,
    seenTodoIds: Set<string>,
): { results: (AgentMessage | AgentTodoGroup)[]; nextGroupIndex: number } {
    const results: (AgentMessage | AgentTodoGroup)[] = [];

    let current: AgentMessage | AgentTodoGroup | null = null;
    let nextAgentMessageId = message.id;
    let nextGroupIndex = groupIndex;
    for (const fragment of message.fragments) {
        // For non-todo fragments we just want to append it to the current message
        if (fragment.type !== "todoItem") {
            if (current === null) {
                current = initializeAgentMessage(fragment, nextAgentMessageId);
                nextAgentMessageId += 1;
            } else if (current.type === "agent") {
                current.fragments.push(fragment);
            } else if (current.type === "todo-group") {
                const inProgress = current.todos.find(
                    (t) => t.todoStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS,
                );
                // For todo-groups we either add it to the todo that's in-progress
                if (inProgress) {
                    inProgress.fragments.push(fragment);
                } else {
                    // or if there are no in-progress todos, we end the todo group and
                    // start an agent message instead.
                    results.push(current);
                    current = initializeAgentMessage(fragment, nextAgentMessageId);
                    nextAgentMessageId += 1;
                }
            }
            continue;
        }

        // For todo fragments we either update the existing group or create a new group
        if (current?.type === "todo-group") {
            updateTodos(current.todos, fragment);
            seenTodoIds.add(fragment.todoItemId);
            continue;
        } else {
            // If we see a pending|in-progress todo and we aren't already in a todo group,
            // then we start a new group.
            if (
                fragment.status === AgentResponseBlock_TodoItem_Phase.PENDING ||
                fragment.status === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS
            ) {
                if (current) results.push(current);
                // Check if this TODO was seen in a previous group
                const isContinuation = seenTodoIds.has(fragment.todoItemId);
                current = {
                    type: "todo-group",
                    groupId: `todo-group-${nextGroupIndex}`,
                    todos: [initializeTodoGroupItem(fragment)],
                    isContinuation,
                };
                nextGroupIndex += 1;
                seenTodoIds.add(fragment.todoItemId);
                continue;
            }

            // Otherwise se just add the fragment to the current message
            if (current) {
                current.fragments.push(fragment);
            }
        }
    }

    if (current) {
        results.push(current);
    }

    return { results, nextGroupIndex };
}

/** Updates the todos so they reflects the state of the latest todo represented by the fragment */
function updateTodos(todos: AgentTodoGroupItem[], fragment: AgentMessageFragmentTodoItem): void {
    for (const todo of todos) {
        if (todo.todoId === fragment.todoItemId) {
            // Don't update the status if it was already done (i.e. don't clear it)
            todo.todoStatus =
                todo.todoStatus !== AgentResponseBlock_TodoItem_Phase.DONE ? fragment.status : todo.todoStatus;
            todo.todoTitle = fragment.title;
            return;
        }
    }
    todos.push(initializeTodoGroupItem(fragment));
}

function initializeTodoGroupItem(fragment: AgentMessageFragmentTodoItem): AgentTodoGroupItem {
    return {
        todoId: fragment.todoItemId,
        todoStatus: fragment.status,
        todoTitle: fragment.title,
        fragments: [],
    };
}

function initializeAgentMessage(fragment: AgentMessageFragment, nextAgentMessageId: number): AgentMessage {
    return {
        type: "agent",
        id: nextAgentMessageId,
        fragments: [fragment],
    };
}

export function calculateTodoGroupProgress(todos: AgentTodoGroupItem[]): { started: number; total: number } {
    const total = todos.length;
    const pending = todos.filter((t) => t.todoStatus === AgentResponseBlock_TodoItem_Phase.PENDING).length;
    return { started: total - pending, total };
}

/** Updates TODO statuses in grouped messages to reflect their final state across all original messages */
function applyFinalTodoStates(groupedMessages: GroupedMessage[], originalMessages: Message[]): GroupedMessage[] {
    // Build map of final TODO states from original messages
    const finalStates = new Map<string, AgentResponseBlock_TodoItem_Phase>();
    for (const message of originalMessages) {
        if (!isAgentResponse(message)) continue;
        for (const fragment of message.fragments) {
            if (fragment.type === "todoItem") {
                finalStates.set(fragment.todoItemId, fragment.status);
            }
        }
    }

    // Update each TODO group's items with final states
    for (const group of groupedMessages) {
        if (group.type !== "todo-group") continue;
        for (const todo of group.todos) {
            const finalState = finalStates.get(todo.todoId);
            if (finalState !== undefined && todo.todoStatus !== AgentResponseBlock_TodoItem_Phase.DONE) {
                todo.todoStatus = finalState;
            }
        }
    }

    return groupedMessages;
}
